import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  UserPlus,
  Mail,
  Users,
  ShieldAlert,
  ShieldCheck,
  ExternalLink,
  Loader2,
  ClipboardList,
} from 'lucide-react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { fetchTicketSummaries, TICKETS_PAGE_LIMIT } from '@/lib/ticketsApi';
import {
  WORKFLOW_STAGE,
  getWorkflowStageLabel,
  isPendingApproval,
  isWorkflowDecided,
  isWorkflowTicket,
  summarizeWorkflowForApproval,
} from '@/lib/workflowHelpers';
import { WORKFLOW_TYPE_FFID, isFfidTicketCandidate } from '@/lib/workflowTypes';

const API = INCIDENTS_API;
const POLL_MS = 8000;

function isFfidTicket(ticket, summary) {
  // Trust resolved workflow type first (same as ticket detail). Do not let a
  // ticket_number like FFID_* override a concrete NUP/TER classification.
  const fromSummary = summary?.workflowType?.id;
  if (fromSummary === WORKFLOW_TYPE_FFID) return true;
  if (fromSummary && fromSummary !== 'workflow') return false;

  const fromState = String(ticket?.workflow_state?.workflow_type || '').trim();
  if (fromState === WORKFLOW_TYPE_FFID) return true;
  if (fromState) return false;

  return isFfidTicketCandidate(ticket);
}

function actionUrlForTicket(ticket, summary) {
  const tn = ticket?.ticket_number;
  if (!tn) return null;
  if (isFfidTicket(ticket, summary)) {
    return `${API}/ffid/${tn}/action`;
  }
  return `${API}/workflow/${tn}/action`;
}

/** Lean GET /tickets list omits workflow_state — approval cards need a full GET. */
function needsWorkflowHydration(ticket) {
  if (!ticket?.ticket_number || !ticket.workflow_stage) return false;
  const state = ticket.workflow_state;
  return !(state && typeof state === 'object');
}

async function fetchFullTicket(ticketNumber, { preferFfidApi = false } = {}) {
  const tn = encodeURIComponent(ticketNumber);
  if (preferFfidApi) {
    try {
      const { data } = await axios.get(`${API}/ffid/${tn}`);
      if (data) return data;
    } catch {
      /* fall through — GET /tickets still allowed for FFID */
    }
  }
  const { data } = await axios.get(`${API}/tickets/${tn}`);
  return data || null;
}

async function hydrateApprovalTickets(items, { canFfidUpdate = false } = {}) {
  const list = Array.isArray(items) ? items : [];
  const pending = list.filter(needsWorkflowHydration);
  if (pending.length === 0) return list;

  const results = await Promise.all(
    pending.map(async (row) => {
      try {
        const preferFfid = canFfidUpdate && isFfidTicketCandidate(row);
        const full = await fetchFullTicket(row.ticket_number, { preferFfidApi: preferFfid });
        return full || row;
      } catch (err) {
        console.warn(`Could not hydrate approval ticket ${row.ticket_number}`, err);
        return row;
      }
    }),
  );
  const byNumber = new Map(results.map((t) => [t.ticket_number, t]));
  return list.map((row) => byNumber.get(row.ticket_number) || row);
}

function formatWhen(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function SodBadge({ summary }) {
  if (!summary.showsSod) return null;
  if (summary.sodSkipped) {
    return <Badge variant="outline">SoD skipped</Badge>;
  }
  if (summary.hasSodConflicts) {
    return (
      <Badge className="bg-cp-crit text-white">
        {summary.sodConflictCount} SoD conflict{summary.sodConflictCount === 1 ? '' : 's'}
      </Badge>
    );
  }
  return <Badge className="bg-cp-ok text-white">SoD clear</Badge>;
}

function fieldIcon(key) {
  const k = String(key || '').toLowerCase();
  if (k.includes('email') || k.includes('mail')) return Mail;
  if (k.includes('reference') || k.includes('template')) return Users;
  if (k.includes('user') || k.includes('employee') || k.includes('identity')) return UserPlus;
  return ClipboardList;
}

function WorkflowSummaryCard({
  ticket,
  onApprove,
  onReject,
  busy,
  showActions,
  canWrite = true,
  canFfidUpdate = false,
}) {
  const navigate = useNavigate();
  const summary = summarizeWorkflowForApproval(ticket);
  const type = summary.workflowType;
  const stageLabel = getWorkflowStageLabel(ticket);
  const fieldCols = Math.min(Math.max(summary.primaryFields.length, 1), 3);
  const ffid = isFfidTicket(ticket, summary);
  const canAct = ffid ? canFfidUpdate : canWrite;
  const detailPath = ffid
    ? `/ffid/${summary.ticketNumber}`
    : `/tickets/${summary.ticketNumber}`;

  return (
    <Card
      className={
        isPendingApproval(ticket)
          ? 'ticket-card ticket-card--approval'
          : summary.stage === WORKFLOW_STAGE.DONE || summary.stage === WORKFLOW_STAGE.SCHEDULED
            ? 'ticket-card ticket-card--done'
            : summary.stage === WORKFLOW_STAGE.REJECTED ||
                summary.stage === WORKFLOW_STAGE.FAILED
              ? 'ticket-card ticket-card--failed'
              : summary.stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED
                ? 'ticket-card ticket-card--failed'
                : 'ticket-card'
      }
      data-testid={`workflow-approval-${summary.ticketNumber}`}
    >
      <CardHeader>
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className="font-mono text-xs">
                {summary.ticketNumber}
              </Badge>
              <Badge className="bg-cp-violet text-white text-xs" title={type.label}>
                {type.shortLabel}
              </Badge>
              <Badge
                className={`text-xs ${
                  isPendingApproval(ticket)
                    ? 'bg-cp-imp text-white'
                    : summary.stage === WORKFLOW_STAGE.DONE ||
                        summary.stage === WORKFLOW_STAGE.SCHEDULED
                      ? 'bg-cp-ok text-white'
                      : summary.stage === WORKFLOW_STAGE.REJECTED ||
                          summary.stage === WORKFLOW_STAGE.FAILED
                        ? 'bg-cp-crit text-white'
                        : summary.stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED
                          ? 'bg-cp-crit text-white'
                          : summary.stage === WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION
                            ? 'bg-cp-violet text-white'
                            : 'bg-cp-surface2 text-cp-muted'
                }`}
              >
                {stageLabel}
              </Badge>
              <SodBadge summary={summary} />
            </div>
            <CardTitle className="text-xl">{summary.title}</CardTitle>
            <CardDescription className="mt-1 line-clamp-2">
              {summary.description || type.description}
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            title="Open ticket"
            onClick={() => navigate(detailPath)}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {summary.primaryFields.length > 0 ? (
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${fieldCols}, minmax(0, 1fr))` }}
          >
            {summary.primaryFields.map((field) => {
              const Icon = fieldIcon(field.key);
              return (
                <div
                  key={field.key}
                  className="rounded-lg border border-cp-line bg-cp-inset p-3"
                >
                  <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-cp-faint">
                    <Icon className="h-3.5 w-3.5" /> {field.label}
                  </div>
                  <div className="mt-1 font-mono text-sm font-semibold text-cp-fg break-all">
                    {field.value || '—'}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {(summary.showsRoles || summary.showsSod) ? (
          <div
            className={`grid gap-3 ${
              summary.showsRoles && summary.showsSod ? 'md:grid-cols-2' : ''
            }`}
          >
            {summary.showsRoles ? (
              <div className="rounded-lg border border-cp-line p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-cp-fg">
                  <ClipboardList className="h-4 w-4 text-cp-violet" />
                  {summary.workflowType?.id === 'access_modification' ? 'Role changes' : 'Roles to copy'}
                  <Badge variant="secondary" className="ml-auto text-[10.5px]">
                    {summary.roleCount}
                  </Badge>
                </div>
                {summary.roles.length > 0 ? (
                  <ul className="max-h-28 space-y-1 overflow-y-auto text-xs font-mono text-cp-muted">
                    {summary.roles.slice(0, 8).map((role) => {
                      const isAdd = role.startsWith('+ ');
                      const isRemove = role.startsWith('- ');
                      return (
                        <li
                          key={role}
                          className={`truncate ${
                            isAdd ? 'text-cp-ok' : isRemove ? 'text-cp-crit' : ''
                          }`}
                        >
                          {role}
                        </li>
                      );
                    })}
                    {summary.roles.length > 8 ? (
                      <li className="text-cp-faint">+{summary.roles.length - 8} more</li>
                    ) : null}
                  </ul>
                ) : (
                  <p className="text-sm text-cp-faint">No roles captured yet.</p>
                )}
              </div>
            ) : null}

            {summary.showsSod ? (
              <div
                className={`rounded-lg border p-3 ${
                  summary.hasSodConflicts
                    ? 'border-cp-crit bg-[color-mix(in_srgb,var(--crit)_8%,var(--surface))]'
                    : 'border-cp-line'
                }`}
              >
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-cp-fg">
                  {summary.hasSodConflicts ? (
                    <ShieldAlert className="h-4 w-4 text-cp-crit" />
                  ) : (
                    <ShieldCheck className="h-4 w-4 text-cp-ok" />
                  )}
                  SoD check
                </div>
                {summary.hasSodConflicts ? (
                  <ul className="space-y-1 text-xs text-cp-crit">
                    {summary.sodConflicts.map((c, idx) => (
                      <li key={idx} className="flex gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          {typeof c === 'string'
                            ? c
                            : c?.message || c?.conflict || JSON.stringify(c)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-cp-muted">
                    {summary.sodSkipped
                      ? 'SoD check was not run for this request.'
                      : 'No segregation-of-duties conflicts detected.'}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {summary.summaryPreview && !summary.showsRoles && !summary.showsSod ? (
          <div className="rounded-lg border border-cp-line bg-cp-inset p-3 text-sm text-cp-muted whitespace-pre-wrap">
            {summary.summaryPreview}
          </div>
        ) : null}

        {summary.approval?.by || summary.approval?.at ? (
          <div className="rounded-lg border border-cp-line bg-cp-inset px-3 py-2 text-sm text-cp-muted">
            <span className="font-semibold text-cp-fg">
              {summary.approval.status === 'approved'
                ? 'Approved'
                : summary.approval.status === 'rejected'
                  ? 'Rejected'
                  : 'Decision'}
            </span>
            {summary.approval.by ? ` by ${summary.approval.by}` : ''}
            {summary.approval.at ? ` • ${formatWhen(summary.approval.at)}` : ''}
            {summary.approval.comment ? (
              <div className="mt-1 text-xs text-cp-faint">“{summary.approval.comment}”</div>
            ) : null}
          </div>
        ) : null}

        {showActions ? (
          <div className="flex flex-wrap gap-2 pt-1">
            <span
              title={permissionDeniedTitle(canAct, 'approve this request')}
              className="flex-1 min-w-[140px]"
            >
              <Button
                className="w-full"
                disabled={busy || !canAct}
                onClick={() => onApprove(ticket)}
                data-testid={`workflow-approve-${summary.ticketNumber}`}
              >
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                {summary.approveVerb}
              </Button>
            </span>
            <span
              title={permissionDeniedTitle(canAct, 'reject this request')}
              className="flex-1 min-w-[140px]"
            >
              <Button
                variant="destructive"
                className="w-full"
                disabled={busy || !canAct}
                onClick={() => onReject(ticket)}
                data-testid={`workflow-reject-${summary.ticketNumber}`}
              >
                <XCircle className="mr-2 h-4 w-4" />
                {summary.rejectVerb}
              </Button>
            </span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function ApprovalWorkflow() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission('approvals', 'read');
  const canWrite = hasPermission('approvals', 'write')
    || hasPermission('approvals', 'update')
    || hasPermission('approvals', 'create');
  const canFfidUpdate = hasPermission('ffid', 'update');
  const { user } = useAuth();
  const approver = user?.email || user?.username || 'Current User';

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyTicket, setBusyTicket] = useState(null);
  const [dialog, setDialog] = useState({ open: false, ticket: null, action: 'approve' });
  const [comments, setComments] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  const fetchData = useCallback(async () => {
    if (!canRead) {
      setTickets([]);
      setLoading(false);
      return;
    }
    try {
      // Workflow approvals come from tickets; the legacy GET /approvals list API is gone (404).
      // Poll page 1 only without include_total (lean envelope + no COUNT on poll).
      // Lean TicketSummary has workflow_stage but not workflow_state — hydrate so
      // FFID/NUP approval cards show extracted fields (proposed FFID, window, etc.).
      const page = await fetchTicketSummaries(API, {
        limit: TICKETS_PAGE_LIMIT,
        offset: 0,
        includeTotal: false,
      });
      const items = await hydrateApprovalTickets(page.items, { canFfidUpdate });
      setTickets(items);
    } catch (error) {
      console.error('Error loading approvals board:', error);
    } finally {
      setLoading(false);
    }
  }, [canRead, canFfidUpdate]);

  useEffect(() => {
    fetchData();
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchData();
    };
    const interval = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData]);

  const pendingWorkflow = useMemo(
    () =>
      tickets.filter(
        (t) =>
          isWorkflowTicket(t) &&
          isPendingApproval(t) &&
          summarizeWorkflowForApproval(t).workflowType?.showsApproval !== false,
      ),
    [tickets],
  );

  const recentDecisions = useMemo(() => {
    return tickets
      .filter(
        (t) =>
          isWorkflowTicket(t) &&
          isWorkflowDecided(t) &&
          summarizeWorkflowForApproval(t).workflowType?.showsApproval !== false,
      )
      .map((t) => ({ ticket: t, summary: summarizeWorkflowForApproval(t) }))
      .sort((a, b) => {
        const at = a.summary.approval?.at || a.summary.updatedAt || '';
        const bt = b.summary.approval?.at || b.summary.updatedAt || '';
        return String(bt).localeCompare(String(at));
      })
      .slice(0, 25);
  }, [tickets]);

  const dialogSummary = useMemo(
    () => summarizeWorkflowForApproval(dialog.ticket || {}),
    [dialog.ticket],
  );

  const openDecisionDialog = async (ticket, action) => {
    let full = ticket;
    if (needsWorkflowHydration(ticket)) {
      try {
        const preferFfid = canFfidUpdate && isFfidTicketCandidate(ticket);
        full = (await fetchFullTicket(ticket.ticket_number, { preferFfidApi: preferFfid })) || ticket;
      } catch (hydrateErr) {
        console.warn('Could not hydrate ticket before approval dialog', hydrateErr);
      }
    }
    setDialog({ open: true, ticket: full, action });
    setComments('');
  };

  const submitDecision = async () => {
    const ticket = dialog.ticket;
    if (!ticket?.ticket_number) return;
    const tn = ticket.ticket_number;
    setBusyTicket(tn);
    try {
      // Lean Approvals list rows often lack workflow_state — load full ticket
      // so FFID cannot be misclassified as a generic workflow approve.
      let full = ticket;
      try {
        const preferFfid = canFfidUpdate && isFfidTicketCandidate(ticket);
        const data = await fetchFullTicket(tn, { preferFfidApi: preferFfid });
        if (data) full = data;
      } catch (hydrateErr) {
        console.warn('Could not hydrate ticket before approval; using list row', hydrateErr);
      }
      const summary = summarizeWorkflowForApproval(full);
      const ffid = isFfidTicket(full, summary);
      if (ffid && !canFfidUpdate) {
        toast.error(
          'Firefighter ID approvals require ffid.update. Ticket-operator alone cannot approve FFID requests.',
        );
        setBusyTicket(null);
        return;
      }
      if (!ffid && !canWrite) {
        setBusyTicket(null);
        return;
      }
      const url = actionUrlForTicket(full, summary);
      const res = await axios.post(url, {
        action: dialog.action,
        approver,
        comment: comments || undefined,
      });
      const nextStage = res.data?.workflow_stage;
      const provisionFailed =
        dialog.action === 'approve' &&
        (res.data?.status === 'error' || nextStage === 'FAILED');
      if (provisionFailed) {
        const isFfid =
          String(res.data?.workflow_state?.workflow_type || '') === 'firefighter_id';
        toast.error(
          res.data?.error ||
            (isFfid
              ? `Approved ${tn}, but Firefighter ID assignment failed.`
              : `Approved ${tn}, but provisioning failed. The SAP user may already exist.`),
        );
      } else {
        toast.success(
          dialog.action === 'approve'
            ? res.data?.assignment_emailed
              ? `Approved ${tn} — Firefighter ID assigned; informational notice emailed`
              : res.data?.password_emailed
                ? `Approved ${tn} — user created, roles copied, and credentials emailed`
                : `Approved ${tn} — user created and roles copied`
            : `Rejected ${tn} as ${approver}`,
        );
      }
      setDialog({ open: false, ticket: null, action: 'approve' });
      setComments('');
      setActiveTab('history');
      await fetchData();
    } catch (error) {
      console.error('Error processing workflow approval:', error);
      const detail = error.response?.data?.detail || error.response?.data?.error;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Failed to apply decision',
      );
      // Decision may still have been persisted server-side — refresh list.
      try {
        await fetchData();
      } catch {
        /* ignore refresh errors */
      }
    } finally {
      setBusyTicket(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-cp-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading approvals…
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="space-y-6" data-testid="approval-workflow">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Approval Workflow</h1>
          <p className="text-muted-foreground mt-2">Review and approve deployment requests</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">You do not have permission to view approvals.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingCount = pendingWorkflow.length;

  return (
    <div className="space-y-6" data-testid="approval-workflow">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-cp-faint mb-1">
            Control plane
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-cp-fg">Approvals</h1>
          <p className="text-cp-muted mt-1.5">
            Review workflow requests awaiting human approval and track decision history
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-cp-muted">
          <Clock className="h-4 w-4" />
          <span>
            {pendingCount} pending
            {pendingWorkflow.length ? ` · ${pendingWorkflow.length} workflow` : ''}
          </span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-cp-inset border border-cp-line">
          <TabsTrigger value="pending">
            Pending
            {pendingWorkflow.length > 0 ? (
              <Badge className="ml-2 bg-cp-imp text-white text-[10px]">
                {pendingWorkflow.length}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="history">Approval history</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4 space-y-4">
          {pendingWorkflow.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="mx-auto mb-4 h-12 w-12 text-cp-ok" />
                <p className="text-cp-muted">No workflow requests waiting for approval</p>
              </CardContent>
            </Card>
          ) : (
            pendingWorkflow.map((ticket) => (
              <WorkflowSummaryCard
                key={ticket.id || ticket.ticket_number}
                ticket={ticket}
                showActions
                canWrite={canWrite}
                canFfidUpdate={canFfidUpdate}
                busy={busyTicket === ticket.ticket_number}
                onApprove={(t) => openDecisionDialog(t, 'approve')}
                onReject={(t) => openDecisionDialog(t, 'reject')}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4">
          {recentDecisions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-cp-muted">
                No completed workflow approvals yet
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-cp-line">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">History</CardTitle>
                  <CardDescription>
                    Recent workflow decisions with approver identity and timestamp
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-cp-line text-left text-[10.5px] uppercase tracking-[0.06em] text-cp-faint">
                          <th className="py-2 pr-3 font-semibold">Ticket</th>
                          <th className="py-2 pr-3 font-semibold">Type</th>
                          <th className="py-2 pr-3 font-semibold">Subject</th>
                          <th className="py-2 pr-3 font-semibold">Decision</th>
                          <th className="py-2 pr-3 font-semibold">Approver</th>
                          <th className="py-2 font-semibold">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentDecisions.map(({ ticket, summary }) => (
                          <tr
                            key={summary.ticketNumber}
                            className="border-b border-cp-line/80 hover:bg-cp-inset"
                          >
                            <td className="py-2.5 pr-3 font-mono text-xs">
                              {summary.ticketNumber}
                            </td>
                            <td className="py-2.5 pr-3">
                              <Badge className="bg-cp-violet text-white text-[10px]">
                                {summary.workflowType.shortLabel}
                              </Badge>
                            </td>
                            <td className="py-2.5 pr-3 font-mono text-xs">
                              {summary.subject || '—'}
                            </td>
                            <td className="py-2.5 pr-3">
                              <Badge
                                className={
                                  summary.stage === WORKFLOW_STAGE.FAILED
                                    ? 'bg-cp-crit text-white'
                                    : summary.approval.status === 'approved'
                                      ? 'bg-cp-ok text-white'
                                      : 'bg-cp-crit text-white'
                                }
                              >
                                {summary.stage === WORKFLOW_STAGE.FAILED
                                  ? 'Approved · failed'
                                  : summary.approval.status || summary.stage}
                              </Badge>
                            </td>
                            <td className="py-2.5 pr-3 text-cp-fg">
                              {summary.approval.by || '—'}
                            </td>
                            <td className="py-2.5 text-cp-muted">
                              {formatWhen(summary.approval.at) || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {recentDecisions.map(({ ticket }) => (
                <WorkflowSummaryCard
                  key={`hist-${ticket.id || ticket.ticket_number}`}
                  ticket={ticket}
                  showActions={false}
                  busy={false}
                  onApprove={() => {}}
                  onReject={() => {}}
                />
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={dialog.open}
        onOpenChange={(open) => {
          if (!open) setDialog({ open: false, ticket: null, action: 'approve' });
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.action === 'approve'
                ? dialogSummary.approveVerb || 'Approve'
                : dialogSummary.rejectVerb || 'Reject'}
            </DialogTitle>
            <DialogDescription>
              {dialog.ticket?.ticket_number
                ? `${dialog.action === 'approve' ? 'Approve' : 'Reject'} ${dialog.ticket.ticket_number} (${dialogSummary.workflowType.shortLabel}) as ${approver}`
                : 'Confirm your decision'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {dialog.ticket ? (
              <div className="rounded-lg border border-cp-line bg-cp-inset p-3 text-sm">
                <div className="font-mono text-xs text-cp-faint mb-1">
                  {dialogSummary.ticketNumber}
                </div>
                {dialogSummary.primaryFields.map((f) => (
                  <div key={f.key}>
                    {f.label}:{' '}
                    <span className="font-semibold">{f.value || '—'}</span>
                  </div>
                ))}
              </div>
            ) : null}
            <div>
              <Label htmlFor="approval-comments">Comment (optional)</Label>
              <Textarea
                id="approval-comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add a note for the audit trail…"
                rows={3}
                className="mt-1.5"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setDialog({ open: false, ticket: null, action: 'approve' })}
              >
                Cancel
              </Button>
              <span
                title={permissionDeniedTitle(
                  isFfidTicket(dialog.ticket, dialogSummary) ? canFfidUpdate : canWrite,
                  dialog.action === 'approve' ? 'approve this request' : 'reject this request',
                )}
                className="flex-1"
              >
                <Button
                  variant={dialog.action === 'approve' ? 'default' : 'destructive'}
                  className="w-full"
                  disabled={
                    Boolean(busyTicket)
                    || !(
                      isFfidTicket(dialog.ticket, dialogSummary)
                        ? canFfidUpdate
                        : canWrite
                    )
                  }
                  onClick={submitDecision}
                >
                  {busyTicket ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : dialog.action === 'approve' ? (
                    <CheckCircle className="mr-2 h-4 w-4" />
                  ) : (
                    <XCircle className="mr-2 h-4 w-4" />
                  )}
                  Confirm {dialog.action === 'approve' ? 'approval' : 'rejection'}
                </Button>
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
